/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react";

import { cssFns } from "@superweb/css";
import {
  icons,
  LinkButton,
  useTypo,
  useUiColors,
  useUiShadows,
} from "@superweb/ui";
import { useLocale } from "@superweb/intl";

import { useMessage } from "#internal/intl";

export const Form = ({
  header,
  content,
  footer,
  onSubmit,
}: {
  header: {
    title: string;
    step?: { current: number; total: number };
    previousStep?: {
      href: string;
      onClick?: (event: PointerEvent<HTMLAnchorElement>) => void;
    };
  };
  content: ReactNode;
  footer: {
    main: ReactNode;
    bottomSheet?: ReactNode;
    errorMessage?: string;
  };
  onSubmit: () => void;
}) => {
  return (
    <form
      css={{
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        alignContent: "start",
        height: "100%",
      }}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <FormHeader {...header} />
      <div
        css={{
          ...cssFns.padding("18px", "16px"),
          display: "grid",
          alignContent: "start",
          rowGap: "16px",
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        {content}
      </div>
      <FormFooter {...footer} />
    </form>
  );
};

export const FormHeader = ({
  title,
  step,
  previousStep,
}: {
  title: string;
  step?: { current: number; total: number };
  previousStep?: {
    href: string;
    onClick?: (event: PointerEvent<HTMLAnchorElement>) => void;
  };
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const message = useMessage();
  const {
    textInfo: { direction },
  } = useLocale();

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
      }}
    >
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          alignItems: "center",
        }}
      >
        {previousStep && (
          <div css={cssFns.margin("4px")}>
            <LinkButton
              icon={direction === "rtl" ? icons.ArrowRight : icons.ArrowLeft}
              variant="ghost"
              shape="circle"
              link={previousStep}
              ariaLabel={message({
                id: "51669aad-bb04-45e1-8a8d-87752ca913f7",
                context: "Form. Header. Back button",
                default: "Back to previous step",
              })}
            />
          </div>
        )}
        <span
          css={{
            ...cssFns.padding("18px", "16px"),
            ...(previousStep && { paddingInlineStart: "0" }),
            ...typo({
              level: "body1",
              weight: "medium",
              density: "tight",
            }),
            color: uiColors.text,
          }}
        >
          {title}
        </span>
      </div>
      {step ? (
        <div
          css={{
            marginBlockEnd: "4px",
            ...cssFns.padding("0", "20px"),
          }}
        >
          <StepIndicator {...step} />
        </div>
      ) : null}
    </div>
  );
};

const StepIndicator = ({
  current,
  total,
}: {
  current: number;
  total: number;
}) => {
  const uiColors = useUiColors();

  return (
    <div
      css={{
        width: "calc(100% + 40px)",
        height: "4px",
        display: "grid",
        gridTemplateColumns: `repeat(${total}, 1fr)`,
        ...cssFns.margin("0", "-20px"),
      }}
    >
      {Array.from({ length: total }, (_, index) => index).map((index) => (
        <div
          key={index}
          css={{
            backgroundColor:
              current - 1 >= index ? uiColors.text : uiColors.controlMinor,
          }}
        />
      ))}
    </div>
  );
};

const FormFooter = ({
  main,
  bottomSheet,
  errorMessage,
}: {
  main: ReactNode;
  bottomSheet?: ReactNode;
  errorMessage?: string;
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const uiShadows = useUiShadows();

  const ref = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([event]) => setIsSticky(event ? event.intersectionRatio < 1 : false),
      { threshold: 1 },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={ref}
      role="contentinfo"
      css={{
        display: "grid",
        backgroundColor: uiColors.background,
        rowGap: "4px",
        position: "sticky",
        // Threshold for hitting bottom of form
        bottom: "-1px",
        ...cssFns.border({ radius: "16px" }),
        ...cssFns.padding("8px"),
        ...(isSticky && {
          borderBottomLeftRadius: "0px",
          borderBottomRightRadius: "0px",
          boxShadow: uiShadows.topNormal,
          backgroundColor: uiColors.backgroundFloating,
        }),
      }}
    >
      {main}
      {bottomSheet !== undefined && (
        <div
          css={{
            ...cssFns.padding("10px", "8px"),
            paddingBlockEnd: "14px",
            textAlign: "center",
            ...typo({
              level: "caption1",
              weight: "regular",
              density: "tight",
            }),
          }}
        >
          {bottomSheet}
        </div>
      )}
      {errorMessage ? (
        <div
          role={"alert"}
          aria-live={"assertive"}
          css={{
            ...typo({
              level: "caption1",
              weight: "regular",
              density: "tight",
            }),
            textAlign: "center",
            ...cssFns.padding("12px", "0"),
            color: uiColors.statuses.danger,
          }}
        >
          {errorMessage}
        </div>
      ) : null}
    </footer>
  );
};
